import { computed, reactive } from 'vue'

import { MultiSelectObjectOption } from '@collector/shared-ui'
import { Incident } from '@collector/sportsapi-client'
import { Confirmation } from '@collector/sportsapi-client-legacy'
import {
  useParticipants,
  useSportMaps,
} from '@desktop/views/Relation/composables'
import {
  useEventStatuses,
  useIncidentName,
  useIncidentPlayers,
} from '@desktop/views/Relation/Sports/composables'
import { isIncidentIdsGroup } from '@desktop/views/Relation/Sports/configuration'
import { injectHistoryFiltersConfig } from '@desktop/views/Relation/Sports/provide-inject'

import { isPlayerSelectOptionMatched } from '../../../../../utils'
import { IncidentFilterSelect } from '../../types'
import {
  hasIncidentAttribute,
  hasIncidentConfirmation,
  hasIncidentEventStatus,
  hasIncidentId,
  hasIncidentParticipantId,
  hasIncidentParticipantOrAssistant,
} from './filterFunctions'

type UseIncidentFiltersSelect = {
  incidentFiltersSelect: IncidentFilterSelect[]
}

export function useIncidentFiltersSelect(): UseIncidentFiltersSelect {
  // composables
  const { getIncidentName } = useIncidentName()
  const { homeParticipant, awayParticipant } = useParticipants()
  const { eventStatuses } = useEventStatuses()
  const { homePlayersOptions, awayPlayersOptions } = useIncidentPlayers()
  const { incidentAttributeIdToAttributeMap } = useSportMaps()

  // inject
  const {
    historyFiltersIncidents,
    historyFiltersAttributes,
    withConfirmation,
  } = injectHistoryFiltersConfig() || {
    historyFiltersIncidents: [],
    withConfirmation: false,
  }

  // data
  const historyFiltersIncidentsOptions: MultiSelectObjectOption[] =
    historyFiltersIncidents.map((value) => ({
      name: isIncidentIdsGroup(value) ? value.name : getIncidentName(value),
      value: isIncidentIdsGroup(value) ? value.incidentIds : value,
    }))

  const historyFiltersAttributesOptions: MultiSelectObjectOption[] =
    historyFiltersAttributes
      ? historyFiltersAttributes.map((attributeName) => ({
          name: attributeName,
          value: attributeName,
        }))
      : []

  // state
  const incidentFiltersSelect: IncidentFilterSelect[] = [
    {
      name: 'Participant',
      filterFunction: hasIncidentParticipantId,
      options: computed(() => {
        const home = homeParticipant.value
        const away = awayParticipant.value
        if (!home || !away) return []
        return [
          {
            name: home.short_name,
            value: home.id,
          },
          {
            name: away.short_name,
            value: away.id,
          },
        ]
      }),
    },
    {
      name: 'Person',
      withFilter: true,
      value: [],
      filterFunction: hasIncidentParticipantOrAssistant,
      filterPredicate: isPlayerSelectOptionMatched,
      options: computed(() => [
        ...homePlayersOptions.value,
        ...awayPlayersOptions.value,
      ]),
    },
    {
      name: 'Incident',
      withFilter: true,
      value: [],
      filterFunction: hasIncidentId,
      options: historyFiltersIncidentsOptions,
    },
    ...(historyFiltersAttributes && historyFiltersAttributesOptions.length > 0
      ? [
          {
            name: 'Attributes',
            withFilter: true,
            value: [],
            filterFunction: (incident: Incident, attributeNames: Set<string>) =>
              hasIncidentAttribute(
                incident,
                attributeNames,
                incidentAttributeIdToAttributeMap.value,
              ),
            options: historyFiltersAttributesOptions,
          },
        ]
      : []),
    {
      name: 'Status',
      withFilter: true,
      value: [],
      filterFunction: hasIncidentEventStatus,
      options: eventStatuses,
    },
    ...(withConfirmation
      ? [
          {
            name: 'Confirmation',
            filterFunction: hasIncidentConfirmation,
            options: [
              {
                name: 'Tbd',
                value: Confirmation.ToBeDone,
              },
              {
                name: 'Confirmed',
                value: Confirmation.Confirmed,
              },
              {
                name: 'Cancelled',
                value: Confirmation.Cancelled,
              },
            ],
          },
        ]
      : []),
  ].map(reactive)

  return {
    incidentFiltersSelect,
  }
}
